import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from './ui/Modal';
import DbCellCodeEditor from './dbManager/DbCellCodeEditor';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { isBytesTag, isUndecoded } from './dbCellTags';
import DbFkDictionary from './DbFkDictionary';
import DbEnumSelect from './dbManager/DbEnumSelect';
import { detectViewMode, formatForMode, base64ToHexDump, LARGE_VALUE_CHARS } from './dbValueView';
import { reindentJson, compactJson } from '../utils/jsonReindent';

/**
 * HeidiSQL-style cell detail viewer/editor for the DB Manager grid: long
 * TEXT/JSON values get a real monospace textarea instead of the one-line
 * in-place input, plus an explicit "Set NULL" (the inline editor's empty
 * string means empty string — NULL is only ever staged explicitly).
 *
 * Save does NOT write to the database: it stages the value into the grid's
 * pendingEdits exactly like inline editing (Ctrl+S commits the batch).
 * On read-only grids (query tabs) it is a pure viewer.
 *
 * The display controls — how to read the value, whether to wrap it, and
 * pretty-printing — live here rather than in a persistent side panel. A cell is
 * something you open, look at, and close; a panel pinned beside the grid spent
 * most of its life showing whichever cell was last clicked.
 */

const VIEW_MODE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
  { value: 'binary', label: 'Binary' },
];

export default function DbCellDetailModal({
  open,
  columnName,
  columnType,
  value,
  readOnly,
  onClose,
  onSave,
  // Present only when this column carries a foreign key on a table tab, which
  // is what lets the value be chosen by meaning rather than typed as a number.
  fk,
  fkContext,
  // ENUM column of a table tab: { values, nullable }. The value is then chosen
  // from the allowed list (the editor below stays for reading it).
  enumMeta = null,
}) {
  // value: the cell's effective value (pending edit applied) — string | number
  // | boolean | object | null. Editing operates on its string form.
  const initialText = useMemo(() => {
    if (value === null || value === undefined) return '';
    // Binary has no text form; show the hex dump, read-only.
    if (isBytesTag(value)) return base64ToHexDump(value.b64);
    if (isUndecoded(value)) return '';
    return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  }, [value]);

  const opaque = isBytesTag(value) || isUndecoded(value);
  const tooLarge = initialText.length > LARGE_VALUE_CHARS;
  const editable = !readOnly && !opaque && !tooLarge;
  const detected = detectViewMode(value, columnType);

  // JSON opens pretty-printed (owner, 06.10.2026: "it recognises the JSON but
  // does not format it when I open the cell"), and the pretty print is for
  // DISPLAY only — it never changes how the value is stored:
  //  - re-indented by text (jsonReindent), never parse → stringify, so escapes,
  //    duplicate keys and big numbers are shown exactly as stored;
  //  - Stage without an edit stages the stored text, byte for byte;
  //  - an edit to a value stored on one line is saved compact again
  //    (compactJson: whitespace outside strings only). A value that already had
  //    its own line breaks is saved as typed.
  // Not XML: its whitespace can be content.
  const openText = useMemo(() => {
    if (detected !== 'json' || opaque || tooLarge || value == null || typeof value === 'object') return initialText;
    try {
      return reindentJson(initialText);
    } catch {
      return initialText;
    }
  }, [detected, opaque, tooLarge, value, initialText]);
  const storedOnOneLine = !/[\r\n]/.test(initialText);
  const textToStore = (edited) => {
    if (edited === openText) return initialText;
    if (detected === 'json' && openText !== initialText && storedOnOneLine) {
      try {
        return compactJson(edited);
      } catch {
        return edited; // not valid JSON any more: what was typed
      }
    }
    return edited;
  };

  const [text, setText] = useState(openText);
  const [isNull, setIsNull] = useState(value === null);
  const [modeOverride, setModeOverride] = useState(null);
  const [wordWrap, setWordWrap] = useState(true);

  const mode = modeOverride || detected;

  useEffect(() => {
    if (open) {
      setText(openText);
      setIsNull(value === null || value === undefined);
      setModeOverride(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openText]);

  if (!open) return null;

  const charCount = isNull ? 0 : text.length;
  const canFormat = editable && !isNull && (mode === 'json' || mode === 'xml');

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={readOnly ? `View cell — ${columnName}` : `Edit cell — ${columnName}`}
      subtitle={
        isNull
          ? 'Value is NULL'
          : `${charCount.toLocaleString()} character${charCount === 1 ? '' : 's'}`
      }
      // Wide: values are code-like (XML, JSON) and long (owner, 30.09.2026).
      size="5xl"
    >
      <div>
        <div className="dbm-cell-modal-toolbar">
          <CompactSelectPicker
            className="dbm-cell-modal-mode"
            value={mode}
            onChange={setModeOverride}
            options={VIEW_MODE_OPTIONS}
            menuMinWidth={120}
            aria-label="How to display this value"
          />
          <label className="dbm-cell-modal-check">
            <input
              type="checkbox"
              className="checkbox-brand"
              checked={wordWrap}
              onChange={(e) => setWordWrap(e.target.checked)}
            />
            Wrap
          </label>
          {canFormat && (
            <button
              type="button"
              className="dbm-pager-btn"
              onClick={() => setText((t) => formatForMode(t, mode))}
              title="Pretty-print"
            >
              Format
            </button>
          )}
          {isBytesTag(value) && (
            <span className="dbm-cell-modal-note">Binary, {value.len} bytes — hex, read-only</span>
          )}
          {isUndecoded(value) && (
            <span className="dbm-cell-modal-note">
              {value.__undecoded} could not be decoded — read-only
            </span>
          )}
          {tooLarge && !opaque && (
            <span className="dbm-cell-modal-note">Very large value — read-only</span>
          )}
        </div>

        {enumMeta && editable && (
          <div className="dbm-cell-modal-enum">
            <span className="dbm-cell-modal-enum-label">Value</span>
            <DbEnumSelect
              className="dbm-cell-modal-enum-select"
              meta={enumMeta}
              columnName={columnName}
              value={isNull ? null : text}
              onChange={(v) => {
                if (v === null) {
                  setIsNull(true);
                } else {
                  setIsNull(false);
                  setText(v);
                }
              }}
            />
          </div>
        )}

        {/* Monaco: highlighting for the display mode, its own find (Ctrl+F)
            and scrollbars. NULL shows an empty read-only editor with a label. */}
        <div className="dbm-cell-modal-editor-wrap" data-testid="dbm-cell-editor">
          <DbCellCodeEditor
            value={isNull ? '' : text}
            onChange={setText}
            mode={mode}
            wordWrap={wordWrap}
            readOnly={!editable || isNull}
          />
          {isNull && <div className="dbm-cell-modal-null">NULL</div>}
        </div>

        {fk && fkContext && editable && (
          <DbFkDictionary
            engine={fkContext.engine}
            extensionId={fkContext.extensionId}
            connId={fkContext.connId}
            dbName={fkContext.dbName}
            fk={fk}
            value={isNull ? null : text}
            onPick={(key) => {
              // Choosing a row stages its key and clears NULL — picking a
              // reference and leaving the cell NULL would be contradictory.
              setIsNull(false);
              setText(key);
            }}
          />
        )}

        <div className="modal-footer-row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, flex: 1 }}>
            {!readOnly && !opaque && (
              <button
                type="button"
                className="dbm-pager-btn"
                title={isNull ? 'Restore the text value' : 'Stage NULL for this cell'}
                onClick={() => setIsNull((n) => !n)}
              >
                {isNull ? 'Unset NULL' : '∅ Set NULL'}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
              disabled={!editable && !isNull}
              onClick={() => {
                // The pretty print never reaches the database (see openText).
                onSave(isNull ? null : textToStore(text));
                onClose();
              }}
            >
              Stage change
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
